import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { downloadCSV } from '@/lib/csv';

interface ContactMessage {
  id: number;
  user_id: number;
  name: string | null;
  email: string | null;
  subject: string | null;
  message: string | null;
  statusdemande: number | null;
  created_at: string;
}

type StatusFilter = 'all' | 'new' | 'read' | 'done';

const STATUS_META: Record<number, { label: string; badge: string; dot: string }> = {
  0: { label: 'Nouveau', badge: 'bg-primary-100 text-primary-700', dot: 'bg-primary-500' },
  1: { label: 'Lu', badge: 'bg-secondary-100 text-secondary-700', dot: 'bg-secondary-500' },
  2: { label: 'Traité', badge: 'bg-accent-100 text-accent-700', dot: 'bg-accent-500' },
};

function statusOf(m: ContactMessage) {
  return STATUS_META[m.statusdemande ?? 0] || STATUS_META[0];
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Volet « Messages de contact » du Service Client.
 * Affiche les messages envoyés depuis le formulaire de contact du site
 * public, avec gestion du statut (nouveau / lu / traité) et suppression.
 */
export default function ContactMessagesPanel() {
  const { user } = useAuth();
  const merchantId = user?.id ?? null;

  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(async () => {
    if (!merchantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('contact_messages')
        .select('*')
        .eq('user_id', merchantId)
        .order('created_at', { ascending: false });
      if (err) throw err;
      const list = (data || []) as ContactMessage[];
      setMessages(list);
      setSelectedId((prev) => (prev && list.some((m) => m.id === prev) ? prev : list[0]?.id ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors du chargement des messages.');
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const counts = useMemo(
    () => ({
      all: messages.length,
      new: messages.filter((m) => (m.statusdemande ?? 0) === 0).length,
      read: messages.filter((m) => (m.statusdemande ?? 0) === 1).length,
      done: messages.filter((m) => (m.statusdemande ?? 0) === 2).length,
    }),
    [messages],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return messages.filter((m) => {
      const st = m.statusdemande ?? 0;
      if (filter === 'new' && st !== 0) return false;
      if (filter === 'read' && st !== 1) return false;
      if (filter === 'done' && st !== 2) return false;
      if (!q) return true;
      return (
        (m.name || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.subject || '').toLowerCase().includes(q) ||
        (m.message || '').toLowerCase().includes(q)
      );
    });
  }, [messages, filter, search]);

  const selected = messages.find((m) => m.id === selectedId) || null;

  const updateStatus = useCallback(async (m: ContactMessage, status: number) => {
    setBusy(true);
    try {
      const { error: err } = await supabase.from('contact_messages').update({ statusdemande: status }).eq('id', m.id);
      if (err) throw err;
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, statusdemande: status } : x)));
      window.dispatchEvent(new Event('contact-messages-changed'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la mise à jour.');
    } finally {
      setBusy(false);
    }
  }, []);

  const handleMarkAllDone = useCallback(async () => {
    const pending = messages.filter((m) => (m.statusdemande ?? 0) !== 2).length;
    if (pending === 0) return;
    if (!window.confirm(`Marquer ${pending} message${pending > 1 ? 's' : ''} comme traité${pending > 1 ? 's' : ''} ?`)) return;
    setBusy(true);
    try {
      const { error: err } = await supabase
        .from('contact_messages')
        .update({ statusdemande: 2 })
        .eq('user_id', merchantId)
        .neq('statusdemande', 2);
      if (err) throw err;
      setMessages((prev) => prev.map((x) => ({ ...x, statusdemande: 2 })));
      window.dispatchEvent(new Event('contact-messages-changed'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la mise à jour.');
    } finally {
      setBusy(false);
    }
  }, [messages, merchantId]);

  const handleExportCsv = useCallback(() => {
    const headers = ['Nom', 'Email', 'Sujet', 'Message', 'Statut', 'Date'];
    const rows = filtered.map((m) => [
      m.name || '',
      m.email || '',
      m.subject || '',
      m.message || '',
      statusOf(m).label,
      formatDate(m.created_at),
    ]);
    downloadCSV('messages-contact.csv', headers, rows);
  }, [filtered]);

  const openMessage = useCallback(
    (m: ContactMessage) => {
      setSelectedId(m.id);
      if ((m.statusdemande ?? 0) === 0) void updateStatus(m, 1);
    },
    [updateStatus],
  );

  const handleDelete = useCallback(async (m: ContactMessage) => {
    if (!window.confirm(`Supprimer le message de ${m.name || m.email || 'ce contact'} ?`)) return;
    setBusy(true);
    try {
      const { error: err } = await supabase.from('contact_messages').delete().eq('id', m.id);
      if (err) throw err;
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      setSelectedId((prev) => (prev === m.id ? null : prev));
      window.dispatchEvent(new Event('contact-messages-changed'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la suppression.');
    } finally {
      setBusy(false);
    }
  }, []);

  const replyHref = (m: ContactMessage) => {
    const subj = encodeURIComponent(`Re : ${m.subject || 'Votre message'}`);
    const body = encodeURIComponent(`\n\n\n---\n${m.message || ''}`);
    return `mailto:${m.email || ''}?subject=${subj}&body=${body}`;
  };

  const FILTERS: { key: StatusFilter; label: string; count: number }[] = [
    { key: 'all', label: 'Tous', count: counts.all },
    { key: 'new', label: 'Nouveaux', count: counts.new },
    { key: 'read', label: 'Lus', count: counts.read },
    { key: 'done', label: 'Traités', count: counts.done },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          <span className="flex-1">{error}</span>
          <button onClick={fetchData} className="text-red-600 underline cursor-pointer whitespace-nowrap">
            Réessayer
          </button>
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Messages', value: counts.all, icon: 'ri-mail-line', tone: 'bg-secondary-100 text-secondary-700' },
          { label: 'Nouveaux', value: counts.new, icon: 'ri-mail-unread-line', tone: 'bg-primary-100 text-primary-700' },
          { label: 'Lus', value: counts.read, icon: 'ri-mail-open-line', tone: 'bg-secondary-100 text-secondary-700' },
          { label: 'Traités', value: counts.done, icon: 'ri-check-double-line', tone: 'bg-accent-100 text-accent-700' },
        ].map((s) => (
          <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-lg p-3.5 flex items-center gap-3">
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${s.tone}`}>
              <i className={s.icon}></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs text-foreground-500 truncate">{s.label}</p>
              <p className="text-lg font-bold font-heading text-foreground-950">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <button
          onClick={handleMarkAllDone}
          disabled={busy || counts.done === counts.all}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <i className="ri-check-double-line"></i>Tout marquer comme traité
        </button>
        <button
          onClick={handleExportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          <i className="ri-download-2-line"></i>Exporter CSV
        </button>
      </div>

      {/* Filtres + recherche */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit overflow-x-auto max-w-full">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                filter === f.key ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {f.label}
              <span className={`px-1.5 rounded-full text-[10px] ${filter === f.key ? 'bg-primary-100 text-primary-700' : 'bg-background-200/70 text-foreground-500'}`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un message"
            className="w-full pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
          />
        </div>
      </div>

      {messages.length === 0 ? (
        <div className="flex flex-col items-center py-20 bg-background-50 border border-background-200/70 rounded-lg text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-mail-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm text-foreground-500">Aucun message de contact reçu</p>
          <p className="text-xs text-foreground-400 mt-1">Les messages envoyés depuis le formulaire de contact de votre site apparaîtront ici.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4 items-start">
          {/* Liste */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
              <span className="text-sm font-semibold text-foreground-800">{filtered.length} message{filtered.length !== 1 ? 's' : ''}</span>
              <i className="ri-list-unordered text-foreground-400"></i>
            </div>
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center px-4">
                <i className="ri-filter-off-line text-2xl text-foreground-300 mb-2"></i>
                <p className="text-xs text-foreground-500">Aucun message pour ce filtre</p>
              </div>
            ) : (
              <div className="divide-y divide-background-200/70 max-h-[560px] overflow-y-auto">
                {filtered.map((m) => {
                  const st = statusOf(m);
                  const active = m.id === selectedId;
                  const unread = (m.statusdemande ?? 0) === 0;
                  return (
                    <button
                      key={m.id}
                      onClick={() => openMessage(m)}
                      className={`w-full text-left px-4 py-3 transition-colors cursor-pointer ${active ? 'bg-primary-50/70' : 'hover:bg-background-100'}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {unread && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${st.dot}`}></span>}
                        <span className={`flex-1 min-w-0 text-sm truncate ${unread ? 'font-semibold text-foreground-950' : 'font-medium text-foreground-800'}`}>
                          {m.name || m.email || 'Visiteur'}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] whitespace-nowrap ${st.badge}`}>{st.label}</span>
                      </div>
                      <p className="text-xs font-medium text-foreground-700 truncate">{m.subject || 'Sans objet'}</p>
                      <p className="text-xs text-foreground-400 truncate mt-0.5">{m.message || ''}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Détail */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden min-h-[320px]">
            {selected ? (
              <div className="flex flex-col h-full">
                <div className="px-5 py-4 border-b border-background-200/70">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-base font-bold font-heading text-foreground-950 truncate">{selected.subject || 'Sans objet'}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-foreground-500">
                        <span className="flex items-center gap-1"><i className="ri-user-3-line"></i>{selected.name || 'Visiteur'}</span>
                        {selected.email && (
                          <a href={`mailto:${selected.email}`} className="flex items-center gap-1 no-underline hover:text-primary-600 whitespace-nowrap">
                            <i className="ri-mail-line"></i>{selected.email}
                          </a>
                        )}
                      </div>
                      <p className="text-[11px] text-foreground-400 mt-1">{formatDate(selected.created_at)}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] whitespace-nowrap ${statusOf(selected).badge}`}>{statusOf(selected).label}</span>
                  </div>
                </div>

                <div className="p-5 flex-1">
                  <p className="text-sm text-foreground-800 leading-relaxed whitespace-pre-wrap break-words">{selected.message || '—'}</p>
                </div>

                <div className="flex items-center gap-2 px-5 py-3 border-t border-background-200/70 flex-wrap">
                  {selected.email && (
                    <a
                      href={replyHref(selected)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer no-underline whitespace-nowrap"
                    >
                      <i className="ri-reply-line"></i>Répondre
                    </a>
                  )}
                  {(selected.statusdemande ?? 0) !== 2 ? (
                    <button
                      onClick={() => updateStatus(selected, 2)}
                      disabled={busy}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      <i className="ri-check-double-line"></i>Marquer traité
                    </button>
                  ) : (
                    <button
                      onClick={() => updateStatus(selected, 0)}
                      disabled={busy}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      <i className="ri-arrow-go-back-line"></i>Marquer non lu
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(selected)}
                    disabled={busy}
                    className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-foreground-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    <i className="ri-delete-bin-line"></i>Supprimer
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-center px-6">
                <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-mail-open-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">Sélectionnez un message pour le lire.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}